import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';

type Profile = {
  id: string;
  display_name?: string | null;
  username?: string | null;
  profile_image?: string | null;
  strava_link?: string | null;
};

type Attendee = Profile & { status: 'going' | 'interested' };

type RunInfo = {
  title?: string | null;
  day_of_week: string;
  start_time?: string | null;
  location_address?: string | null;
};

type ClubInfo = {
  name: string;
  logo?: string | null;
};

function initialsFromName(name: string) {
  const parts = name.trim().split(' ').filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatTime(time?: string | null) {
  if (!time) return 'Time TBD';
  const trimmed = time.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const parts = trimmed.split(':');
  if (parts.length < 2) return trimmed;
  const hour = Number(parts[0]);
  if (Number.isNaN(hour)) return trimmed;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${parts[1]} ${suffix}`;
}

function formatDate(iso: string) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function Avatar({ name, image }: { name: string; image?: string | null }) {
  if (image) {
    return (
      <img
        src={image}
        alt={name}
        className="h-12 w-12 rounded-full border border-black/10 object-cover"
      />
    );
  }
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black text-sm font-semibold text-white">
      {initialsFromName(name)}
    </div>
  );
}

function AttendeeRow({ attendee }: { attendee: Attendee }) {
  const name = attendee.display_name || attendee.username || 'Runner';
  const profileHref = attendee.username
    ? `/profile/${attendee.username}`
    : `/profile/${attendee.id}`;

  return (
    <div className="flex items-center gap-3 rounded-2xl border border-black/8 bg-white p-4 shadow-sm">
      <Link to={profileHref}>
        <Avatar name={name} image={attendee.profile_image} />
      </Link>

      <div className="flex-1">
        <Link to={profileHref} className="text-sm font-semibold text-black hover:underline">
          {name}
        </Link>
        {attendee.strava_link && (
          <a
            href={attendee.strava_link}
            target="_blank"
            rel="noreferrer"
            className="mt-0.5 block text-xs text-black/50 hover:text-black"
          >
            Strava ↗
          </a>
        )}
      </div>

      <span
        className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
          attendee.status === 'going'
            ? 'bg-[#dcfce7] text-green-800'
            : 'bg-[#fefce8] text-yellow-800'
        }`}
      >
        {attendee.status === 'going' ? '✓ Going' : '★ Interested'}
      </span>
    </div>
  );
}

export default function AttendingPage() {
  const { clubId, runId } = useParams<{ clubId: string; runId: string }>();
  const [searchParams] = useSearchParams();
  const date = searchParams.get('date') ?? '';

  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [run, setRun] = useState<RunInfo | null>(null);
  const [club, setClub] = useState<ClubInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId || !runId || !date) return;

    async function load() {
      setLoading(true);

      const [rsvpResult, runResult, clubResult] = await Promise.all([
        supabase
          .from('run_rsvps')
          .select('user_id, status')
          .eq('run_id', runId)
          .eq('run_date', date)
          .in('status', ['going', 'interested']),

        supabase
          .from('runs')
          .select('title, day_of_week, start_time, location_address')
          .eq('id', runId)
          .single(),

        supabase
          .from('clubs')
          .select('name, logo')
          .eq('id', clubId)
          .single(),
      ]);

      if (runResult.data) setRun(runResult.data as RunInfo);
      if (clubResult.data) setClub(clubResult.data as ClubInfo);

      const rsvps = (rsvpResult.data ?? []) as { user_id: string; status: string }[];

      if (rsvps.length === 0) {
        setAttendees([]);
        setLoading(false);
        return;
      }

      const userIds = rsvps.map((r) => r.user_id);
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, display_name, username, profile_image, strava_link')
        .in('id', userIds);

      const profilesById = ((profilesData ?? []) as Profile[]).reduce<
        Record<string, Profile>
      >((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {});

      const merged: Attendee[] = rsvps
        .map((rsvp) => {
          const profile = profilesById[rsvp.user_id];
          if (!profile) return null;
          return { ...profile, status: rsvp.status as 'going' | 'interested' };
        })
        .filter(Boolean) as Attendee[];

      // Going first, then interested
      merged.sort((a, b) => {
        if (a.status === b.status) return 0;
        return a.status === 'going' ? -1 : 1;
      });

      setAttendees(merged);
      setLoading(false);
    }

    load();
  }, [clubId, runId, date]);

  const goingList = attendees.filter((a) => a.status === 'going');
  const interestedList = attendees.filter((a) => a.status === 'interested');

  const runTitle = run?.title || 'Run';
  const dateLabel = date ? formatDate(date) : '';

  return (
    <main className="min-h-screen bg-gradient-to-br from-[#fffdf3] via-[#fcfcfb] to-[#faf2fd] pb-16">
      <div className="mx-auto max-w-2xl px-4 pt-6 sm:px-6">

        {/* Back link */}
        <Link
          to={`/clubs/${clubId}`}
          className="mb-6 inline-flex items-center gap-2 text-sm font-medium text-black/55 transition hover:text-black"
        >
          ← Back to club
        </Link>

        {/* Header */}
        <div className="mb-6 overflow-hidden rounded-3xl border border-black/10 bg-white p-6 shadow-sm">
          {club && (
            <div className="mb-4 flex items-center gap-3">
              {club.logo && (
                <img
                  src={club.logo}
                  alt={club.name}
                  className="h-10 w-10 rounded-xl border border-black/10 object-cover"
                />
              )}
              <p className="text-sm font-medium text-black/55">{club.name}</p>
            </div>
          )}

          <h1 className="text-2xl font-bold tracking-tight text-black">
            {runTitle}
          </h1>

          {dateLabel && (
            <p className="mt-1 text-sm text-black/60">{dateLabel}</p>
          )}

          {run?.start_time && (
            <p className="mt-0.5 text-sm text-black/60">
              🕒 {formatTime(run.start_time)}
              {run.location_address && ` · 📍 ${run.location_address.split(',')[0]}`}
            </p>
          )}

          {!loading && (
            <div className="mt-4 flex gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-black">{goingList.length}</p>
                <p className="text-xs font-medium text-black/50">Going</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-black">{interestedList.length}</p>
                <p className="text-xs font-medium text-black/50">Interested</p>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl bg-black/5" />
            ))}
          </div>
        ) : attendees.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-black/12 bg-white/75 p-10 text-center text-sm text-black/55">
            No one has RSVPd yet. Be the first!
          </div>
        ) : (
          <div className="space-y-6">
            {goingList.length > 0 && (
              <div>
                <h2 className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-black/40">
                  Going · {goingList.length}
                </h2>
                <div className="space-y-2">
                  {goingList.map((a) => (
                    <AttendeeRow key={a.id} attendee={a} />
                  ))}
                </div>
              </div>
            )}

            {interestedList.length > 0 && (
              <div>
                <h2 className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-black/40">
                  Interested · {interestedList.length}
                </h2>
                <div className="space-y-2">
                  {interestedList.map((a) => (
                    <AttendeeRow key={a.id} attendee={a} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
